// web-frontend/src/shared/constants/legalContent.js
// 이용약관 / 개인정보처리방침 전문 — 단일 출처(SSOT) + 테넌트별 처리자(controller) 파라미터화.
// RegisterPage 와 AppSettingsPage 가 getLegalContent(tenantId, lang) 로 공유한다.
//
// [2026-06-12] 1차 법무 보강: 제3자제공 구체화, 위탁/국외이전/자동화결정/만14세 신설, 관할·변경고지 보강.
// [2026-06-14] 2차 보강 (사용자 결정 반영):
//   - 처리자(controller) 명의를 테넌트별로 파라미터화. PIPA 상 처리 목적을 정하는 주최사(고객사)가 개인정보처리자.
//     · DSK / DSK2027 → 주식회사 벡스코(BEXCO)   · AP(홍콩) → 행사 주최사(법인명 미확정 — 운영주체 확인 후 교체)
//   - 위탁(제5조)에 플랫폼 운영사 "지구를구하는인간(NET ZERO SAPIENS)" 추가 (controller=고객사, 우리=수탁자 구조).
//   - 개인정보 보호책임자(제13조): 별도 CPO 미지정 → 대표자 겸임으로 서술 (성명 미발명).
//   - 제4조: 비즈니스 매칭 제3자 제공은 가입 시 별도 동의(비즈니스 회원)로 취득함을 명시.
//   - 약관/처리방침 ja/zh 전문 추가 (수기 번역, 외부 번역 API 미사용).
//   - {{CONTROLLER}} / {{DPO}} 토큰은 getLegalContent 가 테넌트·언어별로 치환한다.
// [2026-06-15] 3차 보강 (개인정보 운영리스크 정비 — 사용자 승인):
//   - 제2조에 수집 출처 고지(개인정보보호법 제20조 — 행사 주최사 제공 계정) 신설.
//   - 제5조(위탁)·제6조(국외이전)에 실사용 외부 생성형 AI(Google LLC=Gemini 챗봇 주력, OpenAI=챗봇 보조·AI 큐레이터·이미지 검증·번역 보조) 추가.
//     · 매칭/벡터 임베딩은 로컬(BGE-M3/Weaviate, localhost) 처리라 외부 전송 없음 → 고지 제외(코드 검증).
//   - AI 처리는 서비스 필수 기능 → 별도 동의 신설 없이 필수 개인정보 동의 + 본 처리방침 고지로 갈음.
//   ⚠ OpenAI/Google 수탁자 연락처·보유기간은 계약 확정 후 갱신. 국외이전 항목 변경은 중요사항 → 시행 30일 전 사전고지 운영 필요.
// ⚠ AP 처리자 법인명, 대표자 성명, 회사명 표기(벡스코 vs 운영주체)는 법무 확정 후 갱신 필요.
// [2026-07-28] DSK/DSK2027 DPO 연락처를 플레이스홀더(privacy@dsk.co.kr — 미존재 주소)에서
//   드론쇼코리아 사무국 공식 연락처로 교체. 출처: droneshowkorea.com 메인/참가안내 +
//   eng.droneshowkorea.com Exhibit 페이지 ("DSK 2027 Secretariat", BEXCO 사무동 4층,
//   droneshow@bexco.co.kr · dsk_bexco@naver.com · 051-740-7409). BEXCO 도메인 주소를 대표로 사용.

import { getTenantId } from '../config/tenant';

const SUPPORTED = ['ko', 'en', 'ja', 'zh'];

// 테넌트별 개인정보처리자(controller) — 처리 목적을 정하는 주최/주관(고객사).
const CONTROLLER = {
  DSK:     { ko: '주식회사 벡스코', en: 'BEXCO Co., Ltd.', ja: '株式会社BEXCO', zh: 'BEXCO股份有限公司', dpo: 'droneshow@bexco.co.kr (051-740-7409)' },
  DSK2027: { ko: '주식회사 벡스코', en: 'BEXCO Co., Ltd.', ja: '株式会社BEXCO', zh: 'BEXCO股份有限公司', dpo: 'droneshow@bexco.co.kr (051-740-7409)' },
  // ⚠ AP(홍콩) 처리자 법인명·연락처 미확정 — 행사 주최사로 임시 표기. 운영주체 확인 후 교체 필요.
  AP:      { ko: 'AI Power+ 주최사', en: 'the AI Power+ Organizer', ja: 'AI Power+主催者', zh: 'AI Power+主办方', dpo: 'privacy@dsk.co.kr' },
};

function controllerFor(tenantId) {
  return CONTROLLER[String(tenantId || '').toUpperCase()] || CONTROLLER.DSK;
}
function fill(text, c, lang) {
  return String(text)
    .split('{{CONTROLLER}}').join(c[lang] || c.ko)
    .split('{{DPO}}').join(c.dpo);
}

const TERMS_RAW = {
  ko: `제1조 (목적)
본 약관은 DSK 플랫폼(이하 "서비스")의 이용과 관련하여 {{CONTROLLER}}(이하 "회사")와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.
제2조 (정의)
1. "회원"이란 본 약관에 동의하고 회사와 이용계약을 체결한 자를 말합니다.
2. "서비스"란 회사가 제공하는 전시회 관람, 비즈니스 매칭, 미팅 예약 등 모든 관련 서비스를 의미합니다.
3. "일반 관람객"이란 전시회 관람 및 기본 서비스를 이용하는 회원을 말합니다.
4. "비즈니스 회원"이란 비즈니스 방문객, 바이어, 참가 기업 등 사무국 승인을 거쳐 비즈니스 기능을 이용하는 회원을 말합니다.
제3조 (서비스 이용계약의 성립)
1. 이용계약은 회원이 되고자 하는 자가 본 약관에 동의하고 회사가 정한 절차에 따라 회원가입을 완료함으로써 성립합니다.
2. 비즈니스 회원의 경우 사무국의 승인 절차를 거쳐야 모든 서비스를 이용할 수 있습니다.
3. 회사는 다음 각 호에 해당하는 경우 회원가입을 거부하거나 승인을 취소할 수 있습니다.
   - 타인의 정보를 도용한 경우
   - 허위 정보를 기재한 경우
   - 과거에 회원 자격을 상실한 적이 있는 경우
   - 기타 회원으로 등록하는 것이 부적절한 경우
제4조 (서비스의 제공)
1. 회사는 다음과 같은 서비스를 제공합니다.
   - 전시회 정보 제공 및 관람 서비스
   - 기업 간 비즈니스 매칭 서비스
   - 미팅 예약 및 관리 서비스
   - 실시간 채팅 및 커뮤니케이션 서비스
   - AI 기반 기업 추천 서비스
   - 기타 회사가 정하는 서비스
2. 서비스는 연중무휴 24시간 제공함을 원칙으로 합니다. 다만, 시스템 점검 등의 필요에 의해 일시적으로 서비스가 중단될 수 있습니다.
제5조 (회원의 의무)
1. 회원은 관계 법령, 본 약관의 규정, 이용안내 등을 준수하여야 합니다.
2. 회원은 자신의 계정 정보를 안전하게 관리해야 하며, 이를 제3자에게 양도하거나 대여할 수 없습니다.
3. 회원은 서비스를 이용하면서 다음 행위를 해서는 안 됩니다.
   - 타인의 정보 도용 또는 허위 정보 등록
   - 서비스 운영을 방해하는 행위
   - 타인의 명예를 훼손하거나 불이익을 주는 행위
   - 음란물, 불법 정보 등을 게시하거나 유포하는 행위
   - 회사의 지적재산권을 침해하는 행위
   - 기타 불법적이거나 부당한 행위
제6조 (서비스 이용 제한)
1. 회사는 회원이 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 서비스 이용을 제한하거나 회원 자격을 정지 또는 상실시킬 수 있습니다.
2. 회사는 전항에 따른 조치 시 사전에 회원에게 통지합니다. 다만, 긴급한 경우에는 사후에 통지할 수 있습니다.
제7조 (계약 해지 및 탈퇴)
1. 회원은 언제든지 회사에 탈퇴를 요청할 수 있으며, 회사는 즉시 회원 탈퇴를 처리합니다.
2. 회원이 탈퇴하는 경우, 관련 법령 및 개인정보처리방침에 따라 회사가 보유하는 정보 외의 데이터는 삭제됩니다.
제8조 (저작권 및 지적재산권)
1. 서비스에서 제공되는 모든 콘텐츠의 저작권은 회사에 귀속됩니다.
2. 회원이 서비스 내에 게시한 콘텐츠에 대한 저작권은 해당 회원에게 귀속되며, 회사는 서비스 운영 목적으로 이를 사용할 수 있습니다.
제9조 (면책조항)
1. 회사는 천재지변, 전쟁, 기간통신사업자의 서비스 중단 등 불가항력으로 인해 서비스를 제공할 수 없는 경우에는 책임을 지지 않습니다.
2. 회사는 회원의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다.
3. 회사는 회원이 게시한 정보, 자료의 신뢰도, 정확성에 대해서는 책임을 지지 않습니다.
4. 회사는 회원 간 또는 회원과 제3자 간에 서비스를 매개로 발생한 분쟁에 대해 개입할 의무가 없으며 이로 인한 손해에 대해 책임을 지지 않습니다.
제10조 (손해배상)
1. 회사가 고의 또는 중과실로 회원에게 손해를 끼친 경우, 회사는 그 손해를 배상할 책임이 있습니다.
2. 회원이 본 약관을 위반하여 회사에 손해를 끼친 경우, 해당 회원은 회사에 그 손해를 배상할 책임이 있습니다.
제11조 (분쟁 해결)
1. 회사와 회원 간에 발생한 분쟁에 관한 소송에는 대한민국 법을 적용하며, 관할법원은 민사소송법에 따라 정합니다.
2. 소비자인 회원과 회사 간의 소송은 제소 당시 회원의 주소를 관할하는 지방법원에 제기할 수 있으며, 주소가 없는 경우 거소를 관할하는 지방법원에 제기할 수 있습니다.
3. 회원은 분쟁 해결을 위해 소비자분쟁조정위원회, 콘텐츠분쟁조정위원회 등에 조정을 신청할 수 있습니다.
제12조 (약관의 변경)
1. 회사는 필요한 경우 관련 법령을 위배하지 않는 범위에서 본 약관을 변경할 수 있습니다.
2. 약관이 변경되는 경우, 회사는 변경 내용과 시행일을 시행일 7일 전부터 서비스 내 공지사항을 통해 공지합니다. 다만, 회원에게 불리하거나 중대한 사항의 변경은 시행일 30일 전부터 공지하고, 이메일 등 전자적 수단으로 개별 통지하도록 노력합니다.
3. 회원이 변경된 약관에 동의하지 않는 경우, 시행일 전까지 거부 의사를 표시하고 서비스 이용을 중단하거나 탈퇴할 수 있습니다. 시행일 이후 서비스를 계속 이용하는 경우 변경된 약관에 동의한 것으로 봅니다.
부칙
1. 본 약관은 2025년 1월 1일부터 시행되며, 2026년 6월 14일 일부 개정되었습니다.
2. 본 약관에서 정하지 않은 사항은 관련 법령 및 회사의 정책에 따릅니다.`,

  en: `Article 1 (Purpose)
These Terms and Conditions aim to define the rights, obligations, and responsibilities between {{CONTROLLER}} (hereinafter referred to as the "Company") and the user regarding the use of the DSK platform (hereinafter referred to as the "Service").
Article 2 (Definitions)
1. "Member" refers to a person who has agreed to these Terms and signed a user agreement with the Company.
2. "Service" refers to all related services provided by the Company, including exhibition viewing, business matching, and meeting reservations.
3. "General Visitor" refers to a member who uses basic services such as exhibition viewing.
4. "Business Member" refers to a member who uses business functions after approval by the Secretariat, including Business Visitors, Buyers, and Exhibitors.
Article 3 (Establishment of Service Use Contract)
1. A use contract is established when a person who wishes to become a member agrees to these Terms and completes the registration process prescribed by the Company.
2. Business Members can use all services only after going through the Secretariat's approval process.
3. The Company may refuse registration or cancel approval in the following cases:
   - Using another person's information
   - Providing false information
   - Having previously lost membership status
   - Other cases where registration is deemed inappropriate
Article 4 (Provision of Services)
1. The Company provides the following services:
   - Exhibition information and viewing services
   - Business-to-business matching services
   - Meeting reservation and management services
   - Real-time chat and communication services
   - AI-based company recommendation services
   - Other services determined by the Company
2. Services are provided 24 hours a day, 365 days a year in principle. However, services may be temporarily suspended due to system maintenance or other needs.
Article 5 (Member Obligations)
1. Members must comply with relevant laws, regulations of these Terms, and usage guidelines.
2. Members must safely manage their account information and may not transfer or lend it to third parties.
3. Members must not engage in the following activities while using the service:
   - Using another person's information or registering false information
   - Interfering with service operations
   - Defaming or causing disadvantage to others
   - Posting or distributing obscene or illegal content
   - Infringing on the Company's intellectual property rights
   - Other illegal or unfair activities
Article 6 (Restriction of Service Use)
1. The Company may restrict service use or suspend or revoke membership if a member violates the obligations of these Terms or interferes with normal service operations.
2. The Company will notify members in advance when taking such measures. However, notification may be given afterwards in urgent cases.
Article 7 (Contract Termination and Withdrawal)
1. Members may request withdrawal from the Company at any time, and the Company will process member withdrawal immediately.
2. When a member withdraws, data other than information held by the Company in accordance with relevant laws and the Privacy Policy will be deleted.
Article 8 (Copyright and Intellectual Property Rights)
1. The copyright of all content provided in the service belongs to the Company.
2. The copyright of content posted by members within the service belongs to the respective members, and the Company may use it for service operation purposes.
Article 9 (Disclaimer)
1. The Company is not responsible for failure to provide services due to force majeure such as natural disasters, war, or service interruption by telecommunications carriers.
2. The Company is not responsible for service use failures caused by members' negligence.
3. The Company is not responsible for the reliability or accuracy of information or materials posted by members.
4. The Company has no obligation to intervene in disputes arising through the service between members or between members and third parties, and is not responsible for any resulting damages.
Article 10 (Compensation for Damages)
1. If the Company intentionally or through gross negligence causes damage to a member, the Company is liable to compensate for that damage.
2. If a member violates these Terms and causes damage to the Company, that member is liable to compensate the Company for that damage.
Article 11 (Dispute Resolution)
1. Lawsuits regarding disputes between the Company and members shall be governed by the laws of the Republic of Korea, and the competent court shall be determined in accordance with the Civil Procedure Act.
2. A lawsuit between the Company and a member who is a consumer may be filed with the district court having jurisdiction over the member's address at the time of filing, or, if there is no address, the member's place of residence.
3. Members may apply for mediation to the Consumer Dispute Mediation Committee, the Content Dispute Mediation Committee, etc. to resolve disputes.
Article 12 (Amendment of Terms)
1. The Company may amend these Terms within the scope that does not violate relevant laws if necessary.
2. When the Terms are amended, the Company will announce the changes and the effective date through the service notice board from 7 days before the effective date. However, changes that are unfavorable or material to members will be announced from 30 days before the effective date, and the Company will endeavor to notify members individually by electronic means such as email.
3. If a member does not agree to the amended Terms, they may express their refusal before the effective date and discontinue using the service or withdraw. Continued use of the service after the effective date constitutes agreement to the amended Terms.
Addendum
1. These Terms have been effective from January 1, 2025, and were partially amended on June 14, 2026.
2. Matters not stipulated in these Terms shall be governed by relevant laws and Company policies.`,

  ja: `第1条 (目的)
本規約は、DSKプラットフォーム(以下「サービス」)の利用に関して、{{CONTROLLER}}(以下「会社」)と利用者との間の権利、義務および責任事項を定めることを目的とします。
第2条 (定義)
1. 「会員」とは、本規約に同意し、会社と利用契約を締結した者をいいます。
2. 「サービス」とは、会社が提供する展示会の観覧、ビジネスマッチング、ミーティング予約など、すべての関連サービスを意味します。
3. 「一般来場者」とは、展示会の観覧および基本サービスを利用する会員をいいます。
4. 「ビジネス会員」とは、ビジネスビジター、バイヤー、出展企業など、事務局の承認を経てビジネス機能を利用する会員をいいます。
第3条 (サービス利用契約の成立)
1. 利用契約は、会員になろうとする者が本規約に同意し、会社が定める手続きに従って会員登録を完了することにより成立します。
2. ビジネス会員の場合、事務局の承認手続きを経てはじめてすべてのサービスを利用できます。
3. 会社は、次の各号に該当する場合、会員登録を拒否し、または承認を取り消すことができます。
   - 他人の情報を盗用した場合
   - 虚偽の情報を記載した場合
   - 過去に会員資格を喪失したことがある場合
   - その他、会員として登録することが不適切な場合
第4条 (サービスの提供)
1. 会社は、次のサービスを提供します。
   - 展示会情報の提供および観覧サービス
   - 企業間ビジネスマッチングサービス
   - ミーティング予約および管理サービス
   - リアルタイムチャットおよびコミュニケーションサービス
   - AIに基づく企業推薦サービス
   - その他、会社が定めるサービス
2. サービスは年中無休24時間提供を原則とします。ただし、システム点検等の必要により、一時的にサービスが中断される場合があります。
第5条 (会員の義務)
1. 会員は、関係法令、本規約の規定、利用案内などを遵守しなければなりません。
2. 会員は、自身のアカウント情報を安全に管理しなければならず、これを第三者に譲渡または貸与することはできません。
3. 会員は、サービスを利用するにあたり、次の行為をしてはなりません。
   - 他人の情報の盗用または虚偽情報の登録
   - サービス運営を妨害する行為
   - 他人の名誉を毀損し、または不利益を与える行為
   - わいせつ物、違法情報などを掲載または流布する行為
   - 会社の知的財産権を侵害する行為
   - その他、違法または不当な行為
第6条 (サービス利用の制限)
1. 会社は、会員が本規約の義務に違反し、またはサービスの正常な運営を妨害した場合、サービス利用を制限し、または会員資格を停止もしくは喪失させることができます。
2. 会社は、前項の措置を行う際、事前に会員へ通知します。ただし、緊急の場合は事後に通知することができます。
第7条 (契約の解除および退会)
1. 会員は、いつでも会社に退会を申請でき、会社は直ちに会員退会を処理します。
2. 会員が退会する場合、関係法令および個人情報処理方針に従って会社が保有する情報を除くデータは削除されます。
第8条 (著作権および知的財産権)
1. サービスで提供されるすべてのコンテンツの著作権は会社に帰属します。
2. 会員がサービス内に掲載したコンテンツの著作権は当該会員に帰属し、会社はサービス運営目的でこれを使用できます。
第9条 (免責条項)
1. 会社は、天災地変、戦争、基幹通信事業者のサービス中断など不可抗力によりサービスを提供できない場合は責任を負いません。
2. 会社は、会員の帰責事由によるサービス利用障害について責任を負いません。
3. 会社は、会員が掲載した情報・資料の信頼性、正確性については責任を負いません。
4. 会社は、会員間または会員と第三者との間でサービスを介して発生した紛争に介入する義務を負わず、これによる損害について責任を負いません。
第10条 (損害賠償)
1. 会社が故意または重過失により会員に損害を与えた場合、会社はその損害を賠償する責任があります。
2. 会員が本規約に違反して会社に損害を与えた場合、当該会員は会社にその損害を賠償する責任があります。
第11条 (紛争の解決)
1. 会社と会員との間で生じた紛争に関する訴訟には大韓民国法を適用し、管轄裁判所は民事訴訟法に従って定めます。
2. 消費者である会員と会社との間の訴訟は、提訴時の会員の住所を管轄する地方裁判所に提起することができ、住所がない場合は居所を管轄する地方裁判所に提起できます。
3. 会員は、紛争解決のため、消費者紛争調停委員会、コンテンツ紛争調停委員会などに調停を申請することができます。
第12条 (規約の変更)
1. 会社は、必要な場合、関係法令に違反しない範囲で本規約を変更できます。
2. 規約が変更される場合、会社は変更内容と施行日を施行日の7日前からサービス内のお知らせを通じて告知します。ただし、会員に不利または重大な事項の変更は施行日の30日前から告知し、メール等の電子的手段で個別に通知するよう努めます。
3. 会員が変更後の規約に同意しない場合、施行日前までに拒否の意思を表示し、サービス利用を中止し、または退会できます。施行日以後にサービスを継続して利用する場合、変更後の規約に同意したものとみなします。
附則
1. 本規約は2025年1月1日から施行され、2026年6月14日に一部改正されました。
2. 本規約で定めのない事項は、関係法令および会社の方針に従います。`,

  zh: `第1条 (目的)
本条款旨在规定就DSK平台(以下简称"服务")的使用，{{CONTROLLER}}(以下简称"公司")与用户之间的权利、义务及责任事项。
第2条 (定义)
1. "会员"是指同意本条款并与公司签订使用合同的人。
2. "服务"是指公司提供的展会参观、商务对接、会议预约等所有相关服务。
3. "普通参观者"是指使用展会参观及基本服务的会员。
4. "商务会员"是指商务访客、买家、参展企业等经事务局批准后使用商务功能的会员。
第3条 (服务使用合同的成立)
1. 使用合同自欲成为会员者同意本条款并按照公司规定的程序完成注册时成立。
2. 商务会员须经事务局批准程序后方可使用全部服务。
3. 公司在符合下列任一情形时，可拒绝注册或取消批准：
   - 盗用他人信息的
   - 填写虚假信息的
   - 过去曾丧失会员资格的
   - 其他不适宜注册为会员的情形
第4条 (服务的提供)
1. 公司提供以下服务：
   - 展会信息提供及参观服务
   - 企业间商务对接服务
   - 会议预约及管理服务
   - 实时聊天及沟通服务
   - 基于AI的企业推荐服务
   - 其他由公司确定的服务
2. 服务原则上全年无休、24小时提供。但因系统检修等需要，服务可能临时中断。
第5条 (会员的义务)
1. 会员应遵守相关法令、本条款的规定及使用指南等。
2. 会员应安全管理自己的账户信息，不得将其转让或出借给第三方。
3. 会员在使用服务时不得有下列行为：
   - 盗用他人信息或注册虚假信息
   - 妨碍服务运营的行为
   - 损害他人名誉或造成不利的行为
   - 发布或传播淫秽物、违法信息等的行为
   - 侵犯公司知识产权的行为
   - 其他违法或不正当行为
第6条 (服务使用的限制)
1. 会员违反本条款义务或妨碍服务正常运营的，公司可限制其使用服务，或暂停、取消其会员资格。
2. 公司采取前款措施时，事先通知会员。但情况紧急的，可事后通知。
第7条 (合同解除及退会)
1. 会员可随时向公司申请退会，公司将立即处理会员退会。
2. 会员退会时，除依据相关法令及个人信息处理方针由公司保有的信息外，其余数据将被删除。
第8条 (著作权及知识产权)
1. 服务中提供的所有内容的著作权归公司所有。
2. 会员在服务内发布的内容的著作权归该会员所有，公司可为服务运营目的使用之。
第9条 (免责条款)
1. 因自然灾害、战争、基础电信运营商服务中断等不可抗力导致无法提供服务的，公司不承担责任。
2. 因会员自身原因导致的服务使用障碍，公司不承担责任。
3. 对会员发布的信息、资料的可靠性、准确性，公司不承担责任。
4. 对会员之间或会员与第三方之间通过服务发生的纠纷，公司无介入义务，并对由此产生的损害不承担责任。
第10条 (损害赔偿)
1. 公司因故意或重大过失给会员造成损害的，公司应赔偿该损害。
2. 会员违反本条款给公司造成损害的，该会员应向公司赔偿该损害。
第11条 (纠纷的解决)
1. 公司与会员之间发生的纠纷诉讼适用大韩民国法律，管辖法院依《民事诉讼法》确定。
2. 作为消费者的会员与公司之间的诉讼，可向起诉时会员住所地的地方法院提起；无住所的，可向居所地的地方法院提起。
3. 会员可为解决纠纷，向消费者纠纷调解委员会、内容纠纷调解委员会等申请调解。
第12条 (条款的变更)
1. 公司在必要时可在不违反相关法令的范围内变更本条款。
2. 条款变更时，公司自施行日前7日起通过服务内公告告知变更内容及施行日。但对会员不利或重大事项的变更，自施行日前30日起告知，并努力以邮件等电子方式个别通知。
3. 会员不同意变更后的条款的，可在施行日前表示拒绝并中止使用服务或退会。施行日后继续使用服务的，视为同意变更后的条款。
附则
1. 本条款自2025年1月1日起施行，并于2026年6月14日部分修订。
2. 本条款未尽事宜，依照相关法令及公司方针处理。`,
};

const PRIVACY_RAW = {
  ko: `개인정보처리방침
{{CONTROLLER}}(이하 "회사")는 개인정보보호법 등 관련 법령에 따라 이용자의 개인정보를 보호하고, 이와 관련한 고충을 신속하게 처리하기 위하여 다음과 같이 개인정보처리방침을 수립·공개합니다.
제1조 (개인정보의 수집 및 이용 목적)
회사는 다음의 목적을 위해 개인정보를 수집 및 이용합니다.
1. 회원가입 및 관리
   - 회원 가입의사 확인, 회원제 서비스 제공, 회원자격 유지·관리, 서비스 부정이용 방지
2. 전시회 운영 및 참관 서비스 제공
   - 전시회 입장 관리, 관람 이력 관리, 맞춤형 서비스 제공
3. 비즈니스 매칭 서비스 제공
   - 기업 간 매칭, 미팅 예약 및 관리, 비즈니스 상담 지원
4. 마케팅 및 광고 활용
   - 신규 서비스 안내, 이벤트 정보 제공 (별도의 선택 동의를 받은 경우에 한함)
5. 서비스 개선 및 통계 분석
   - 서비스 이용 통계, 접속빈도 분석, 서비스 개선
제2조 (수집하는 개인정보의 항목 및 동의 거부권)
1. 필수항목
   - 일반 회원: 이름, 이메일, 비밀번호, 전화번호
   - 비즈니스 회원: 이름, 이메일, 비밀번호, 전화번호, 회사명, 직함, 명함 이미지
2. 선택항목
   - 부서, 가입 목적/관심 분야
3. 서비스 이용 과정에서 자동 수집되는 정보
   - IP 주소, 쿠키, 방문 일시, 서비스 이용 기록, 기기 정보
4. 이용자는 선택항목의 수집·이용 및 광고성 정보 수신에 동의하지 않을 권리가 있으며, 동의하지 않아도 회원가입 및 서비스 이용에 제한을 받지 않습니다. 다만 선택 동의를 거부하는 경우 맞춤형 추천, SMS 알림 등 일부 기능의 이용이 제한될 수 있습니다.
5. 광고성 정보의 전송(SMS 등)은 정보통신망법 제50조에 따라 별도의 명시적 사전 동의(옵트인)를 받은 경우에만 이루어지며, 이용자는 알림 설정에서 언제든지 동의를 철회할 수 있습니다.
6. 개인정보의 수집 출처: 회사는 위 정보를 이용자가 회원가입·서비스 이용 과정에서 직접 입력하는 방식으로 수집합니다. 다만 행사 주최사로부터 참가사·바이어의 기본 정보(이름, 소속, 연락처 등)를 제공받아 계정을 생성하는 경우, 해당 정보의 수집 출처는 그 행사 주최사이며, 이용자는 개인정보보호법 제20조에 따라 수집 출처·처리 목적 및 처리정지 요구권에 관한 고지를 회사에 요구할 수 있습니다.
제3조 (개인정보의 보유 및 이용기간)
1. 회원 탈퇴 시까지 보유합니다. 다만, 다음의 경우에는 해당 기간까지 보유합니다.
   - 관계 법령에 따른 보존 의무가 있는 경우
   - 계약 또는 청약철회 등에 관한 기록: 5년
   - 대금결제 및 재화 등의 공급에 관한 기록: 5년
   - 소비자의 불만 또는 분쟁처리에 관한 기록: 3년
   - 접속에 관한 기록: 3개월
2. 전시회 종료 후 1년간 보관 후 파기합니다.
제4조 (개인정보의 제3자 제공)
1. 회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않으며, 제공이 필요한 경우 사전에 정보주체의 동의를 받습니다.
2. 비즈니스 매칭 서비스 이용 시 다음과 같이 개인정보가 제공되며, 이 제공은 회원가입 시 정보주체로부터 받은 별도의 제3자 제공 동의에 근거합니다(비즈니스 회원).
   - 제공받는 자: 매칭이 성립된 상대방 회원(참가 기업, 바이어 등)
   - 제공 목적: 기업 간 비즈니스 매칭, 미팅 주선 및 상담 연결
   - 제공 항목: 회사명, 담당자명, 연락처, 관심분야
   - 보유·이용기간: 매칭 목적 달성 시까지 (최대 전시회 종료 후 1년)
   - 이용자는 제3자 제공에 동의하지 않을 수 있으며, 이 경우 비즈니스 매칭 서비스 이용이 제한될 수 있습니다.
3. 그 밖에 법령의 규정에 따르거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우에는 예외로 합니다.
제5조 (개인정보 처리의 위탁)
1. 회사는 원활한 서비스 제공을 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다.
   - 지구를구하는인간(NET ZERO SAPIENS): 플랫폼(앱·웹) 운영 및 시스템 관리 — 서비스 이용에 필요한 개인정보 전반
   - NHN Cloud(주): SMS(문자) 발송 — 전화번호
   - 주식회사 솔라피(SOLAPI) / 카카오: 카카오 알림톡 발송 — 전화번호, 이름, 미팅 일정 정보
   - Twilio SendGrid(이메일 발송 대행): 이메일 주소, 이름 — ※ 국외 이전(제6조 참조)
   - Amazon Web Services(AWS): 클라우드 인프라 운영 및 데이터 보관 — 서울 리전(ap-northeast-2, 국내) 내 보관
   - 토스페이먼츠(주), (주)카카오페이: 결제 처리(기부 등 결제 이용 시) — 결제 정보
   - Google LLC / OpenAI, L.L.C.: 생성형 AI 처리 — AI 챗봇 응답 생성(주력 Google Gemini, 보조 OpenAI), AI 큐레이션·추천 설명, 이용자가 업로드한 활동 인증 이미지의 인식 기반 검증, 번역 보조 — 이용자가 입력·전송한 문의·대화 텍스트 및 해당 이미지 (※ 국외 이전, 제6조 참조)
2. 회사는 위탁계약 체결 시 개인정보보호법 제26조에 따라 수탁자의 개인정보 보호조치, 재위탁 제한, 관리·감독, 손해배상 등을 계약서 등에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독합니다.
3. 위탁업무의 내용이나 수탁자가 변경될 경우에는 지체없이 본 개인정보처리방침을 통하여 공개합니다.
제6조 (개인정보의 국외 이전)
1. 회사는 서비스 제공 과정에서 다음과 같이 개인정보가 국외로 이전될 수 있습니다.
   - Twilio SendGrid, Inc. (미국)
     · 이전 항목: 이메일 주소, 이름
     · 이전 목적: 인증·안내 이메일 발송
     · 이전 일시 및 방법: 이메일 발송 시 정보통신망을 통한 전송
     · 보유·이용기간: 발송 목적 달성 시까지
   - Google LLC (미국)
     · 이전 항목: (구글 로그인 이용 시) 이메일, 이름, 프로필 이미지 / (푸시 알림 이용 시) 기기 토큰 / (번역·AI 챗봇 이용 시) 번역 대상 텍스트 및 이용자가 입력한 챗봇 대화 내용
     · 이전 목적: 소셜 로그인 인증, 푸시 알림 발송, 다국어 번역 제공, 생성형 AI(Gemini) 기반 챗봇 응답 생성
     · 이전 일시 및 방법: 해당 기능 이용 시 정보통신망을 통한 전송
     · 보유·이용기간: 목적 달성 시까지 (Google 개인정보처리방침에 따름)
   - OpenAI, L.L.C. (미국)
     · 이전 항목: 이용자가 입력·전송한 문의·대화 텍스트, AI 큐레이션 요청 정보, 활동 인증을 위해 업로드한 이미지
     · 이전 목적: 생성형 AI 기반 챗봇(보조)·큐레이션 응답 생성, 이미지 인식 기반 활동 검증, 번역 보조
     · 이전 일시 및 방법: 해당 기능 이용 시 정보통신망을 통한 전송
     · 보유·이용기간: 처리 목적 달성 시까지 (API로 전송된 데이터는 OpenAI의 API 데이터 이용정책에 따라 처리되며 모델 학습에 사용되지 않습니다)
2. 이용자는 개인정보의 국외 이전을 거부할 수 있으며, 거부 시 해당 기능(이메일 알림, 구글 로그인, 푸시 알림, 번역, AI 챗봇·큐레이션·이미지 검증)의 이용이 제한될 수 있습니다. 거부는 개인정보 보호책임자에게 연락하여 요청할 수 있습니다.
제7조 (개인정보의 파기)
1. 회사는 개인정보 보유기간의 경과, 처리 목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체없이 해당 개인정보를 파기합니다.
2. 파기 방법
   - 전자적 파일: 복구 및 재생이 불가능한 방법으로 영구 삭제
   - 종이 문서: 분쇄기로 분쇄하거나 소각
제8조 (정보주체의 권리·의무 및 행사방법)
1. 이용자는 언제든지 자신의 개인정보에 대해 다음의 권리를 행사할 수 있습니다.
   - 개인정보 열람 요구
   - 오류 등이 있을 경우 정정 요구
   - 삭제 요구
   - 처리정지 요구
   - 동의 철회 (광고성 정보 수신 동의 철회는 앱 내 알림 설정에서 직접 가능)
2. 권리 행사는 서면, 전자우편 등을 통해 할 수 있으며, 회사는 이에 대해 지체없이 조치합니다.
제9조 (자동화된 결정에 대한 정보주체의 권리)
1. 회사는 이용자가 입력한 설문·프로필 정보(관심 분야, 관심 제품·기술 등)를 기반으로 AI가 적합한 비즈니스 상대를 추천하는 자동화된 매칭 서비스를 제공합니다.
2. 매칭 추천은 입력 정보와 기업 정보 간의 유사도를 계산하여 추천 후보와 적합도 점수를 제시하는 방식이며, 이용자의 권리·의무에 일방적으로 중대한 영향을 미치는 결정을 자동으로 확정하지 않습니다.
3. 이용자는 개인정보보호법 제37조의2에 따라 자동화된 결정(추천 결과)에 대하여 거부하거나 설명 또는 검토를 요구할 수 있습니다. 요구는 개인정보 보호책임자에게 연락하여 할 수 있으며, 거부 시 매칭 추천 서비스의 제공이 중단될 뿐 그 외 서비스 이용에는 영향이 없습니다.
제10조 (개인정보의 안전성 확보 조치)
회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.
1. 관리적 조치: 내부관리계획 수립·시행, 정기적 직원 교육
2. 기술적 조치: 개인정보처리시스템 접근권한 관리, 접근통제시스템 설치, 고유식별정보 등의 암호화, 보안프로그램 설치
3. 물리적 조치: 전산실, 자료보관실 등의 접근통제
제11조 (개인정보 자동 수집 장치의 설치·운영 및 거부)
1. 회사는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 쿠키(cookie)를 사용합니다.
2. 이용자는 쿠키 설치에 대한 선택권을 가지고 있으며, 웹브라우저 설정을 통해 쿠키를 허용하거나 거부할 수 있습니다. 쿠키 저장을 거부할 경우 일부 서비스 이용에 어려움이 있을 수 있습니다.
제12조 (만 14세 미만 아동의 개인정보)
1. 회사는 만 14세 미만 아동의 개인정보를 수집하지 않으며, 만 14세 미만 아동은 회원으로 가입할 수 없습니다.
2. 만 14세 미만 아동의 개인정보가 수집된 사실이 확인되는 경우, 회사는 지체없이 해당 개인정보를 파기합니다.
제13조 (개인정보 보호책임자)
1. 회사는 개인정보 처리에 관한 업무를 총괄하여 책임지고, 개인정보 처리와 관련한 이용자의 불만처리 및 피해구제 등을 위하여 개인정보 보호책임자를 지정합니다.
2. 별도의 개인정보 보호책임자를 두지 않는 경우 회사의 대표자가 그 직무를 겸임합니다.
▶ 개인정보 보호책임자: 회사 대표자(또는 그가 지정한 자)
- 연락처: {{DPO}}
제14조 (권익침해 구제방법)
이용자는 개인정보침해로 인한 구제를 받기 위하여 개인정보분쟁조정위원회, 한국인터넷진흥원 개인정보침해신고센터 등에 분쟁해결이나 상담 등을 신청할 수 있습니다.
- 개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)
- 개인정보침해신고센터: 118 (privacy.kisa.or.kr)
- 대검찰청: 1301 (www.spo.go.kr)
- 경찰청: 182 (ecrm.cyber.go.kr)
제15조 (개인정보처리방침의 변경)
1. 이 개인정보처리방침은 2025년 1월 1일부터 적용되며, 2026년 6월 15일 개정되었습니다.
2. 방침이 변경되는 경우 시행 7일 전부터 서비스 내 공지를 통해 안내하며, 수집 항목·이용 목적 등 이용자 권리에 중요한 변경이 있는 경우에는 시행 30일 전부터 안내합니다.
3. 이전의 개인정보처리방침은 회사에 요청하여 확인하실 수 있습니다.`,

  en: `Privacy Policy
{{CONTROLLER}} (hereinafter referred to as the "Company") establishes and discloses the following privacy policy to protect users' personal information and promptly handle related grievances in accordance with relevant laws such as the Personal Information Protection Act.
Article 1 (Purpose of Collection and Use of Personal Information)
The Company collects and uses personal information for the following purposes:
1. Membership registration and management
   - Confirmation of registration intent, provision of membership services, maintenance and management of membership, prevention of service misuse
2. Exhibition operation and viewing service provision
   - Exhibition admission management, viewing history management, provision of customized services
3. Provision of business matching services
   - Business-to-business matching, meeting reservation and management, business consultation support
4. Marketing and advertising utilization
   - New service information, event information provision (only with separate optional consent)
5. Service improvement and statistical analysis
   - Service usage statistics, access frequency analysis, service improvement
Article 2 (Items of Personal Information Collected and Right to Refuse Consent)
1. Required Items
   - General Members: Name, Email, Password, Phone Number
   - Business Members: Name, Email, Password, Phone Number, Company Name, Job Title, Business Card Image
2. Optional Items
   - Department, Purpose of registration/Areas of interest
3. Information automatically collected during service use
   - IP address, cookies, visit date and time, service usage records, device information
4. Users have the right to refuse consent to the collection and use of optional items and to the receipt of advertising information, and such refusal does not limit membership registration or use of the service. However, refusing optional consent may limit some features such as personalized recommendations and SMS notifications.
5. Advertising information (such as SMS) is sent only with separate, explicit prior consent (opt-in) in accordance with Article 50 of the Act on Promotion of Information and Communications Network Utilization, and users may withdraw their consent at any time in the notification settings.
6. Source of collection: The Company collects the above information through the user's direct input during sign-up and use of the service. However, when an account is created using participant/buyer basic information (name, affiliation, contact, etc.) provided by the event organizer, the source of that information is the relevant event organizer, and the user may, in accordance with Article 20 of the Personal Information Protection Act, request that the Company disclose the source of collection, the purpose of processing, and the right to request suspension of processing.
Article 3 (Retention and Use Period of Personal Information)
1. Personal information is retained until membership withdrawal. However, it is retained for the following periods in the following cases:
   - When there is a preservation obligation under relevant laws
   - Records related to contracts or subscription withdrawal: 5 years
   - Records related to payment and supply of goods: 5 years
   - Records related to consumer complaints or dispute resolution: 3 years
   - Records related to access: 3 months
2. Personal information is destroyed after being retained for 1 year after the exhibition ends.
Article 4 (Provision of Personal Information to Third Parties)
1. In principle, the Company does not provide users' personal information to third parties, and obtains prior consent from data subjects when provision is necessary.
2. When using the business matching service, personal information is provided as follows, and such provision is based on the separate third-party provision consent obtained from the data subject at sign-up (Business Members):
   - Recipient: Counterpart members with whom matching is established (exhibitors, buyers, etc.)
   - Purpose: Business-to-business matching, meeting arrangement and consultation connection
   - Items: Company name, contact person name, contact information, areas of interest
   - Retention and use period: Until the matching purpose is achieved (up to 1 year after the exhibition ends)
   - Users may refuse consent to third-party provision, in which case use of the business matching service may be limited.
3. Exceptions are made when required by law or requested by investigative agencies according to procedures and methods prescribed by law for investigation purposes.
Article 5 (Entrustment of Personal Information Processing)
1. The Company entrusts personal information processing tasks as follows for smooth service provision:
   - Earth Saving Humans (NET ZERO SAPIENS): platform (app/web) operation and system management — personal information necessary for service use in general
   - NHN Cloud Corp.: SMS (text message) sending — phone number
   - SOLAPI Inc. / Kakao: KakaoTalk notification (Alimtalk) sending — phone number, name, meeting schedule information
   - Twilio SendGrid (email sending agent): email address, name — ※ transferred abroad (see Article 6)
   - Amazon Web Services (AWS): cloud infrastructure operation and data storage — stored in the Seoul region (ap-northeast-2, domestic)
   - Toss Payments Co., Ltd., Kakao Pay Corp.: payment processing (when using payments such as donations) — payment information
   - Google LLC / OpenAI, L.L.C.: generative AI processing — AI chatbot response generation (primarily Google Gemini, with OpenAI as fallback), AI curation and recommendation explanations, recognition-based verification of activity-verification images uploaded by users, and translation assistance — the inquiry/conversation text and related images entered or transmitted by the user (※ transferred abroad, see Article 6)
2. When concluding entrustment contracts, the Company specifies the trustee's personal information protection measures, restrictions on re-entrustment, management and supervision, and liability for damages in accordance with Article 26 of the Personal Information Protection Act, and supervises whether the trustee processes personal information safely.
3. If the content of the entrusted work or the trustee changes, the Company will disclose it through this Privacy Policy without delay.
Article 6 (Cross-Border Transfer of Personal Information)
1. In the course of providing the service, personal information may be transferred abroad as follows:
   - Twilio SendGrid, Inc. (United States)
     · Items transferred: email address, name
     · Purpose: sending verification and notification emails
     · Time and method of transfer: transmitted via information and communications networks when emails are sent
     · Retention and use period: until the sending purpose is achieved
   - Google LLC (United States)
     · Items transferred: (when using Google login) email, name, profile image / (when using push notifications) device token / (when using translation or the AI chatbot) text to be translated and the chatbot conversation content entered by the user
     · Purpose: social login authentication, push notification delivery, multilingual translation, generative-AI (Gemini) based chatbot response generation
     · Time and method of transfer: transmitted via information and communications networks when using the relevant features
     · Retention and use period: until the purpose is achieved (subject to Google's privacy policy)
   - OpenAI, L.L.C. (United States)
     · Items transferred: the inquiry/conversation text entered or transmitted by the user, AI curation request information, and images uploaded for activity verification
     · Purpose: generative-AI based chatbot (fallback) / curation response generation, recognition-based activity verification, and translation assistance
     · Time and method of transfer: transmitted via information and communications networks when using the relevant features
     · Retention and use period: until the processing purpose is achieved (data transmitted via the API is processed under OpenAI's API data usage policy and is not used to train models)
2. Users may refuse the cross-border transfer of their personal information, in which case use of the relevant features (email notifications, Google login, push notifications, translation, AI chatbot/curation/image verification) may be limited. Refusal may be requested by contacting the Personal Information Protection Officer.
Article 7 (Destruction of Personal Information)
1. When personal information becomes unnecessary due to expiration of the retention period or achievement of the processing purpose, the Company destroys the personal information without delay.
2. Destruction methods
   - Electronic files: Permanently deleted in a manner that prevents recovery and reproduction
   - Paper documents: Shredded or incinerated
Article 8 (Rights and Obligations of Data Subjects and Exercise Methods)
1. Users may exercise the following rights regarding their personal information at any time:
   - Request to view personal information
   - Request for correction if there are errors
   - Request for deletion
   - Request for suspension of processing
   - Withdrawal of consent (withdrawal of consent to receive advertising information is available directly in the in-app notification settings)
2. Rights may be exercised through written documents, email, etc., and the Company will take action without delay.
Article 9 (Rights of Data Subjects Regarding Automated Decisions)
1. The Company provides an automated matching service in which AI recommends suitable business counterparts based on survey and profile information entered by users (areas of interest, products and technologies of interest, etc.).
2. Matching recommendations calculate the similarity between the entered information and company information to present recommended candidates and suitability scores, and do not automatically finalize decisions that unilaterally and materially affect users' rights and obligations.
3. In accordance with Article 37-2 of the Personal Information Protection Act, users may refuse automated decisions (recommendation results) or request explanations or reviews. Requests may be made by contacting the Personal Information Protection Officer, and refusal only stops the provision of matching recommendations and does not affect the use of other services.
Article 10 (Measures to Ensure Safety of Personal Information)
The Company takes the following measures to ensure the safety of personal information:
1. Administrative measures: Establishment and implementation of internal management plans, regular employee training
2. Technical measures: Management of access rights to personal information processing systems, installation of access control systems, encryption of unique identification information, installation of security programs
3. Physical measures: Access control to computer rooms, data storage rooms, etc.
Article 11 (Installation, Operation, and Rejection of Automatic Personal Information Collection Devices)
1. The Company uses cookies to provide individual customized services to users.
2. Users have the right to choose whether to install cookies, and may allow or refuse cookies through web browser settings. Refusing cookies may cause difficulties in using some services.
Article 12 (Personal Information of Children Under 14)
1. The Company does not collect personal information of children under 14 years of age, and children under 14 cannot register as members.
2. If it is confirmed that personal information of a child under 14 has been collected, the Company will destroy the information without delay.
Article 13 (Personal Information Protection Officer)
1. The Company designates a Personal Information Protection Officer to take overall responsibility for personal information processing and to handle user complaints and damage relief related to personal information processing.
2. If no separate Personal Information Protection Officer is appointed, the representative of the Company concurrently performs that role.
▶ Personal Information Protection Officer: the Company's representative (or a person designated by the representative)
- Contact: {{DPO}}
Article 14 (Methods for Relief of Rights Infringement)
Users may apply for dispute resolution or consultation to the Personal Information Dispute Mediation Committee, Korea Internet & Security Agency Personal Information Infringement Report Center, etc. for relief from personal information infringement.
- Personal Information Dispute Mediation Committee: 1833-6972 (www.kopico.go.kr)
- Personal Information Infringement Report Center: 118 (privacy.kisa.or.kr)
- Supreme Prosecutors' Office: 1301 (www.spo.go.kr)
- National Police Agency: 182 (ecrm.cyber.go.kr)
Article 15 (Changes to Privacy Policy)
1. This Privacy Policy has been effective from January 1, 2025, and was amended on June 15, 2026.
2. When the policy is changed, notice will be given through the service from 7 days before the effective date; for changes important to users' rights, such as collected items or purposes of use, notice will be given from 30 days before the effective date.
3. Previous versions of the Privacy Policy may be obtained by contacting the Company.`,

  ja: `個人情報処理方針
{{CONTROLLER}}(以下「会社」)は、個人情報保護法など関係法令に従い、利用者の個人情報を保護し、これに関する苦情を迅速に処理するため、次のとおり個人情報処理方針を策定・公開します。
第1条 (個人情報の収集および利用目的)
会社は、次の目的のために個人情報を収集・利用します。
1. 会員登録および管理
   - 会員登録意思の確認、会員制サービスの提供、会員資格の維持・管理、サービスの不正利用防止
2. 展示会の運営および観覧サービスの提供
   - 展示会の入場管理、観覧履歴の管理、カスタマイズサービスの提供
3. ビジネスマッチングサービスの提供
   - 企業間マッチング、ミーティング予約および管理、ビジネス相談支援
4. マーケティングおよび広告活用
   - 新サービスの案内、イベント情報の提供 (別途の選択同意を得た場合に限る)
5. サービス改善および統計分析
   - サービス利用統計、アクセス頻度分析、サービス改善
第2条 (収集する個人情報の項目および同意拒否権)
1. 必須項目
   - 一般会員: 氏名、メール、パスワード、電話番号
   - ビジネス会員: 氏名、メール、パスワード、電話番号、会社名、役職、名刺画像
2. 選択項目
   - 部署、登録目的/関心分野
3. サービス利用過程で自動的に収集される情報
   - IPアドレス、Cookie、訪問日時、サービス利用記録、端末情報
4. 利用者は、選択項目の収集・利用および広告性情報の受信に同意しない権利を有し、同意しなくても会員登録およびサービス利用に制限を受けません。ただし、選択同意を拒否する場合、カスタマイズ推薦、SMS通知など一部機能の利用が制限されることがあります。
5. 広告性情報の送信(SMS等)は、情報通信網法第50条に基づき、別途の明示的な事前同意(オプトイン)を得た場合にのみ行われ、利用者は通知設定からいつでも同意を撤回できます。
6. 個人情報の収集元: 会社は上記情報を、利用者が会員登録・サービス利用の過程で直接入力する方法で収集します。ただし、行事主催者から参加社・バイヤーの基本情報(氏名、所属、連絡先など)の提供を受けてアカウントを生成する場合、当該情報の収集元は当該行事主催者であり、利用者は個人情報保護法第20条に基づき、収集元・処理目的および処理停止要求権に関する告知を会社に要求できます。
第3条 (個人情報の保有および利用期間)
1. 会員退会時まで保有します。ただし、次の場合は当該期間まで保有します。
   - 関係法令により保存義務がある場合
   - 契約または申込撤回などに関する記録: 5年
   - 代金決済および財貨等の供給に関する記録: 5年
   - 消費者の苦情または紛争処理に関する記録: 3年
   - アクセスに関する記録: 3か月
2. 展示会終了後1年間保管した後、破棄します。
第4条 (個人情報の第三者提供)
1. 会社は、原則として利用者の個人情報を第三者に提供せず、提供が必要な場合は事前に情報主体の同意を得ます。
2. ビジネスマッチングサービスの利用時に次のとおり個人情報が提供され、この提供は会員登録時に情報主体から得た別途の第三者提供同意に基づきます(ビジネス会員)。
   - 提供を受ける者: マッチングが成立した相手方会員(出展企業、バイヤー等)
   - 提供目的: 企業間ビジネスマッチング、ミーティングの仲介および相談接続
   - 提供項目: 会社名、担当者名、連絡先、関心分野
   - 保有・利用期間: マッチング目的の達成時まで(最大で展示会終了後1年)
   - 利用者は第三者提供に同意しないことができ、その場合ビジネスマッチングサービスの利用が制限されることがあります。
3. その他、法令の規定による場合、または捜査目的で法令に定められた手続きと方法により捜査機関の要求がある場合は例外とします。
第5条 (個人情報処理の委託)
1. 会社は、円滑なサービス提供のため、次のとおり個人情報処理業務を委託しています。
   - 地球を救う人間(NET ZERO SAPIENS): プラットフォーム(アプリ・ウェブ)運営およびシステム管理 — サービス利用に必要な個人情報全般
   - NHN Cloud(株): SMS(ショートメッセージ)送信 — 電話番号
   - 株式会社SOLAPI / カカオ: カカオ・アリムトーク送信 — 電話番号、氏名、ミーティング日程情報
   - Twilio SendGrid(メール送信代行): メールアドレス、氏名 — ※ 国外移転(第6条参照)
   - Amazon Web Services(AWS): クラウドインフラ運営およびデータ保管 — ソウルリージョン(ap-northeast-2、国内)内に保管
   - トスペイメンツ(株)、(株)カカオペイ: 決済処理(寄付など決済利用時) — 決済情報
   - Google LLC / OpenAI, L.L.C.: 生成AI処理 — AIチャットボットの応答生成(主力Google Gemini、補助OpenAI)、AIキュレーション・推薦の説明、利用者がアップロードした活動認証画像の認識に基づく検証、翻訳の補助 — 利用者が入力・送信した問い合わせ・対話テキストおよび当該画像 (※ 国外移転、第6条参照)
2. 会社は、委託契約締結時、個人情報保護法第26条に基づき、受託者の個人情報保護措置、再委託の制限、管理・監督、損害賠償などを契約書等に明示し、受託者が個人情報を安全に処理しているかを監督します。
3. 委託業務の内容または受託者が変更される場合は、遅滞なく本個人情報処理方針を通じて公開します。
第6条 (個人情報の国外移転)
1. 会社は、サービス提供の過程で次のとおり個人情報が国外に移転されることがあります。
   - Twilio SendGrid, Inc. (米国)
     · 移転項目: メールアドレス、氏名
     · 移転目的: 認証・案内メールの送信
     · 移転日時および方法: メール送信時に情報通信網を通じた送信
     · 保有・利用期間: 送信目的の達成時まで
   - Google LLC (米国)
     · 移転項目: (Googleログイン利用時) メール、氏名、プロフィール画像 / (プッシュ通知利用時) 端末トークン / (翻訳・AIチャットボット利用時) 翻訳対象テキストおよび利用者が入力したチャットボット対話内容
     · 移転目的: ソーシャルログイン認証、プッシュ通知送信、多言語翻訳の提供、生成AI(Gemini)に基づくチャットボット応答生成
     · 移転日時および方法: 当該機能の利用時に情報通信網を通じた送信
     · 保有・利用期間: 目的の達成時まで(Googleの個人情報処理方針による)
   - OpenAI, L.L.C. (米国)
     · 移転項目: 利用者が入力・送信した問い合わせ・対話テキスト、AIキュレーション要求情報、活動認証のためにアップロードした画像
     · 移転目的: 生成AIに基づくチャットボット(補助)・キュレーション応答生成、画像認識に基づく活動検証、翻訳の補助
     · 移転日時および方法: 当該機能の利用時に情報通信網を通じた送信
     · 保有・利用期間: 処理目的の達成時まで(APIを通じて送信されたデータはOpenAIのAPIデータ利用ポリシーに従って処理され、モデルの学習には使用されません)
2. 利用者は個人情報の国外移転を拒否でき、拒否した場合、当該機能(メール通知、Googleログイン、プッシュ通知、翻訳、AIチャットボット・キュレーション・画像検証)の利用が制限されることがあります。拒否は個人情報保護責任者に連絡して要請できます。
第7条 (個人情報の破棄)
1. 会社は、個人情報の保有期間の経過、処理目的の達成など個人情報が不要となったときは、遅滞なく当該個人情報を破棄します。
2. 破棄方法
   - 電子的ファイル: 復元および再生が不可能な方法で永久削除
   - 紙の文書: シュレッダーで裁断または焼却
第8条 (情報主体の権利・義務および行使方法)
1. 利用者は、いつでも自身の個人情報について次の権利を行使できます。
   - 個人情報の閲覧要求
   - 誤り等がある場合の訂正要求
   - 削除要求
   - 処理停止要求
   - 同意の撤回(広告性情報の受信同意の撤回は、アプリ内の通知設定から直接可能)
2. 権利行使は書面、電子メール等を通じて行うことができ、会社はこれに対して遅滞なく措置します。
第9条 (自動化された決定に対する情報主体の権利)
1. 会社は、利用者が入力したアンケート・プロフィール情報(関心分野、関心製品・技術等)に基づき、AIが適切なビジネス相手を推薦する自動化されたマッチングサービスを提供します。
2. マッチング推薦は、入力情報と企業情報との類似度を計算して推薦候補と適合度スコアを提示する方式であり、利用者の権利・義務に一方的に重大な影響を及ぼす決定を自動的に確定するものではありません。
3. 利用者は、個人情報保護法第37条の2に基づき、自動化された決定(推薦結果)に対して拒否し、または説明もしくは検討を要求できます。要求は個人情報保護責任者に連絡して行うことができ、拒否した場合はマッチング推薦サービスの提供が中止されるのみで、その他のサービス利用には影響しません。
第10条 (個人情報の安全性確保措置)
会社は、個人情報の安全性確保のため、次の措置を講じています。
1. 管理的措置: 内部管理計画の策定・施行、定期的な職員教育
2. 技術的措置: 個人情報処理システムのアクセス権限管理、アクセス統制システムの設置、固有識別情報等の暗号化、セキュリティプログラムの設置
3. 物理的措置: サーバー室、資料保管室等へのアクセス統制
第11条 (個人情報の自動収集装置の設置・運営および拒否)
1. 会社は、利用者に個別のカスタマイズサービスを提供するため、Cookieを使用します。
2. 利用者はCookieの設置について選択権を有し、ウェブブラウザの設定を通じてCookieを許可または拒否できます。Cookieの保存を拒否した場合、一部サービスの利用に支障が生じることがあります。
第12条 (満14歳未満の児童の個人情報)
1. 会社は満14歳未満の児童の個人情報を収集せず、満14歳未満の児童は会員として登録できません。
2. 満14歳未満の児童の個人情報が収集された事実が確認された場合、会社は遅滞なく当該個人情報を破棄します。
第13条 (個人情報保護責任者)
1. 会社は、個人情報処理に関する業務を総括して責任を負い、個人情報処理に関する利用者の苦情処理および被害救済などのため、個人情報保護責任者を指定します。
2. 別途の個人情報保護責任者を置かない場合、会社の代表者がその職務を兼任します。
▶ 個人情報保護責任者: 会社の代表者(またはその指定する者)
- 連絡先: {{DPO}}
第14条 (権益侵害の救済方法)
利用者は、個人情報侵害による救済を受けるため、個人情報紛争調停委員会、韓国インターネット振興院 個人情報侵害申告センターなどに紛争解決や相談などを申請できます。
- 個人情報紛争調停委員会: 1833-6972 (www.kopico.go.kr)
- 個人情報侵害申告センター: 118 (privacy.kisa.or.kr)
- 大検察庁: 1301 (www.spo.go.kr)
- 警察庁: 182 (ecrm.cyber.go.kr)
第15条 (個人情報処理方針の変更)
1. この個人情報処理方針は2025年1月1日から適用され、2026年6月15日に改正されました。
2. 方針が変更される場合、施行7日前からサービス内のお知らせを通じて案内し、収集項目・利用目的など利用者の権利に重要な変更がある場合は施行30日前から案内します。
3. 以前の個人情報処理方針は、会社に請求してご確認いただけます。`,

  zh: `个人信息处理方针
{{CONTROLLER}}(以下简称"公司")依据《个人信息保护法》等相关法令，为保护用户的个人信息并迅速处理相关申诉，制定并公开如下个人信息处理方针。
第1条 (个人信息的收集及使用目的)
公司为以下目的收集和使用个人信息：
1. 会员注册及管理
   - 确认注册意愿、提供会员制服务、维持与管理会员资格、防止服务被不正当使用
2. 展会运营及参观服务的提供
   - 展会入场管理、参观记录管理、提供定制化服务
3. 商务对接服务的提供
   - 企业间对接、会议预约及管理、商务洽谈支持
4. 营销及广告利用
   - 新服务介绍、活动信息提供 (仅限取得另行选择同意的情形)
5. 服务改进及统计分析
   - 服务使用统计、访问频率分析、服务改进
第2条 (收集的个人信息项目及拒绝同意权)
1. 必填项
   - 普通会员: 姓名、邮箱、密码、电话号码
   - 商务会员: 姓名、邮箱、密码、电话号码、公司名称、职务、名片图像
2. 选填项
   - 部门、注册目的/关注领域
3. 服务使用过程中自动收集的信息
   - IP地址、Cookie、访问日期时间、服务使用记录、设备信息
4. 用户有权拒绝同意选填项的收集·使用以及广告性信息的接收，即使不同意也不会限制会员注册及服务使用。但拒绝选择性同意的，可能会限制定制化推荐、短信通知等部分功能的使用。
5. 广告性信息的发送(短信等)依据《信息通信网法》第50条，仅在取得另行明示的事先同意(opt-in)时进行，用户可随时在通知设置中撤回同意。
6. 个人信息的收集来源: 公司通过用户在注册·使用服务过程中直接输入的方式收集上述信息。但在接收行事主办方提供的参展商·买家基本信息(姓名、所属、联系方式等)以创建账户的情形下，该信息的收集来源为该行事主办方，用户可依据《个人信息保护法》第20条，要求公司告知收集来源·处理目的及停止处理要求权。
第3条 (个人信息的保留及使用期限)
1. 保留至会员退会时。但下列情形保留至相应期限：
   - 依相关法令负有保存义务的
   - 有关合同或撤回要约等的记录: 5年
   - 有关货款结算及货物等供应的记录: 5年
   - 有关消费者投诉或纠纷处理的记录: 3年
   - 有关访问的记录: 3个月
2. 展会结束后保管1年后予以销毁。
第4条 (个人信息向第三方提供)
1. 公司原则上不向第三方提供用户的个人信息，确需提供时，事先取得信息主体的同意。
2. 使用商务对接服务时，按如下方式提供个人信息，该提供以会员注册时从信息主体取得的另行第三方提供同意为依据(商务会员)。
   - 接收方: 达成对接的对方会员(参展企业、买家等)
   - 提供目的: 企业间商务对接、安排会议及洽谈对接
   - 提供项目: 公司名称、负责人姓名、联系方式、关注领域
   - 保留·使用期限: 至达成对接目的时(最长为展会结束后1年)
   - 用户可不同意第三方提供，此时商务对接服务的使用可能受到限制。
3. 此外，依法令规定，或为侦查目的按照法令规定的程序和方法应侦查机关要求的情形除外。
第5条 (个人信息处理的委托)
1. 公司为顺利提供服务，按如下方式委托个人信息处理业务：
   - 拯救地球的人(NET ZERO SAPIENS): 平台(App/Web)运营及系统管理 — 服务使用所需的个人信息整体
   - NHN Cloud(株): 短信发送 — 电话号码
   - SOLAPI株式会社 / Kakao: Kakao Alimtalk发送 — 电话号码、姓名、会议日程信息
   - Twilio SendGrid(邮件发送代行): 邮箱地址、姓名 — ※ 跨境转移(参见第6条)
   - Amazon Web Services(AWS): 云基础设施运营及数据保管 — 保管于首尔区域(ap-northeast-2，境内)
   - Toss Payments(株)、(株)Kakao Pay: 支付处理(使用捐赠等支付时) — 支付信息
   - Google LLC / OpenAI, L.L.C.: 生成式AI处理 — AI聊天机器人回复生成(主力Google Gemini、辅助OpenAI)、AI策展与推荐说明、对用户上传的活动认证图像的基于识别的核验、翻译辅助 — 用户输入·发送的咨询·对话文本及相关图像 (※ 跨境转移，参见第6条)
2. 公司在签订委托合同时，依据《个人信息保护法》第26条，在合同等中明示受托方的个人信息保护措施、再委托限制、管理·监督、损害赔偿等，并监督受托方是否安全处理个人信息。
3. 委托业务内容或受托方发生变更时，将立即通过本个人信息处理方针予以公开。
第6条 (个人信息的跨境转移)
1. 公司在提供服务过程中，个人信息可能按如下方式转移至境外：
   - Twilio SendGrid, Inc. (美国)
     · 转移项目: 邮箱地址、姓名
     · 转移目的: 发送验证·通知邮件
     · 转移时间及方式: 发送邮件时经由信息通信网传输
     · 保留·使用期限: 至达成发送目的时
   - Google LLC (美国)
     · 转移项目: (使用Google登录时) 邮箱、姓名、头像 / (使用推送通知时) 设备令牌 / (使用翻译·AI聊天机器人时) 待翻译文本及用户输入的聊天机器人对话内容
     · 转移目的: 社交登录认证、推送通知发送、多语言翻译、基于生成式AI(Gemini)的聊天机器人回复生成
     · 转移时间及方式: 使用相应功能时经由信息通信网传输
     · 保留·使用期限: 至达成目的时(依Google个人信息处理方针)
   - OpenAI, L.L.C. (美国)
     · 转移项目: 用户输入·发送的咨询·对话文本、AI策展请求信息、为活动认证而上传的图像
     · 转移目的: 基于生成式AI的聊天机器人(辅助)·策展回复生成、基于图像识别的活动核验、翻译辅助
     · 转移时间及方式: 使用相应功能时经由信息通信网传输
     · 保留·使用期限: 至达成处理目的时(经由API发送的数据依OpenAI的API数据使用政策处理，且不用于训练模型)
2. 用户可拒绝个人信息的跨境转移，拒绝时相应功能(邮件通知、Google登录、推送通知、翻译、AI聊天机器人·策展·图像核验)的使用可能受到限制。可联系个人信息保护负责人提出拒绝请求。
第7条 (个人信息的销毁)
1. 公司在个人信息保留期限届满、处理目的达成等个人信息已无必要时，立即销毁该个人信息。
2. 销毁方法
   - 电子文件: 以无法恢复和再生的方法永久删除
   - 纸质文件: 用碎纸机粉碎或焚烧
第8条 (信息主体的权利·义务及行使方法)
1. 用户可随时就其个人信息行使下列权利：
   - 查阅个人信息
   - 如有错误等，要求更正
   - 要求删除
   - 要求停止处理
   - 撤回同意(撤回广告性信息接收同意，可直接在App内通知设置中进行)
2. 权利行使可通过书面、电子邮件等方式进行，公司将对此立即采取措施。
第9条 (信息主体对自动化决策的权利)
1. 公司基于用户输入的问卷·资料信息(关注领域、关注产品·技术等)，提供由AI推荐合适商务对象的自动化对接服务。
2. 对接推荐采用计算输入信息与企业信息之间相似度，提示推荐候选及适配度分数的方式，并不自动确定对用户权利·义务单方面产生重大影响的决策。
3. 用户可依据《个人信息保护法》第37条之2，对自动化决策(推荐结果)提出拒绝，或要求说明或复核。可联系个人信息保护负责人提出，拒绝时仅停止对接推荐服务的提供，不影响其他服务的使用。
第10条 (个人信息安全性确保措施)
公司为确保个人信息安全，采取如下措施：
1. 管理性措施: 制定·施行内部管理计划，定期开展员工培训
2. 技术性措施: 个人信息处理系统访问权限管理、安装访问控制系统、对唯一识别信息等加密、安装安全程序
3. 物理性措施: 对机房、资料保管室等的访问控制
第11条 (个人信息自动收集装置的设置·运营及拒绝)
1. 公司为向用户提供个性化定制服务，使用Cookie。
2. 用户对Cookie的设置拥有选择权，可通过网页浏览器设置允许或拒绝Cookie。拒绝保存Cookie时，部分服务的使用可能存在不便。
第12条 (未满14周岁儿童的个人信息)
1. 公司不收集未满14周岁儿童的个人信息，未满14周岁儿童不得注册为会员。
2. 经确认收集了未满14周岁儿童个人信息的，公司将立即销毁该个人信息。
第13条 (个人信息保护负责人)
1. 公司总括负责个人信息处理相关业务，为处理用户投诉及救济损害等指定个人信息保护负责人。
2. 未另行设置个人信息保护负责人的，由公司代表人兼任该职务。
▶ 个人信息保护负责人: 公司代表人(或其指定之人)
- 联系方式: {{DPO}}
第14条 (权益侵害救济方法)
用户为就个人信息侵害获得救济，可向个人信息纠纷调解委员会、韩国互联网振兴院 个人信息侵害举报中心等申请纠纷解决或咨询等。
- 个人信息纠纷调解委员会: 1833-6972 (www.kopico.go.kr)
- 个人信息侵害举报中心: 118 (privacy.kisa.or.kr)
- 大检察厅: 1301 (www.spo.go.kr)
- 警察厅: 182 (ecrm.cyber.go.kr)
第15条 (个人信息处理方针的变更)
1. 本个人信息处理方针自2025年1月1日起适用，并于2026年6月15日修订。
2. 方针变更时，自施行前7日起通过服务内公告进行告知；如有收集项目·使用目的等对用户权利有重要影响的变更，则自施行前30日起告知。
3. 以前的个人信息处理方针可向公司请求确认。`,
};

/**
 * 테넌트·언어별로 치환된 약관/처리방침 전문을 반환.
 * @param {string} tenantId 테넌트 id (없으면 활성 테넌트)
 * @param {string} lang 'ko' | 'en' | 'ja' | 'zh'
 * @returns {{terms: string, privacy: string}}
 */
export function getLegalContent(tenantId, lang = 'ko') {
  const tid = tenantId || getTenantId();
  const c = controllerFor(tid);
  const L = SUPPORTED.includes(lang) ? lang : 'ko';
  return {
    terms: fill(TERMS_RAW[L] || TERMS_RAW.ko, c, L),
    privacy: fill(PRIVACY_RAW[L] || PRIVACY_RAW.ko, c, L),
  };
}

// 하위 호환: 활성 테넌트 기준으로 치환된 객체(언어별). 직접 사용 가능하나 신규 코드는 getLegalContent 권장.
export const TERMS_CONTENT = SUPPORTED.reduce((o, l) => { o[l] = getLegalContent(undefined, l).terms; return o; }, {});
export const PRIVACY_CONTENT = SUPPORTED.reduce((o, l) => { o[l] = getLegalContent(undefined, l).privacy; return o; }, {});
